/**
 * overviewStyled.ts — Styled-components de la tab "Overview" del Brand Page.
 *
 * Qué hace: define todos los styled-components usados por BrandTabOverview.tsx
 *   (checklist de getting-started, bloque de auto-fill, grid de imágenes
 *   scrapeadas, shortcuts y danger zone).
 * Qué exporta: cada styled-component con `export const`.
 * Quién lo usa: components/brand/BrandTabOverview.tsx.
 */
import styled from 'styled-components';
import { Card } from '../Card';

export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

// Getting-Started checklist.
export const ChecklistCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  padding: ${({ theme }) => theme.spacing.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

export const ChecklistTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

export const ChecklistSubtitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

export const ChecklistProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${({ theme }) => theme.colors.surfaceHover};
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

export const ChecklistProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.primary},
    ${({ theme }) => theme.colors.secondary || theme.colors.primary}
  );
  border-radius: 4px;
  transition: width 0.5s ease;
`;

export const ChecklistSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
`;

export const ChecklistStep = styled.button<{ $done: boolean; $clickable: boolean }>`
  display: flex;
  align-items: center;
  text-align: left;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: 8px;
  border: 1px solid ${({ theme, $done }) => ($done ? theme.colors.success + '40' : theme.colors.border)};
  background: ${({ theme, $done }) => ($done ? theme.colors.success + '10' : theme.colors.background)};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  ${({ $clickable, theme }) =>
    $clickable &&
    `
    &:hover {
      transform: translateY(-1px);
      box-shadow: ${theme.shadows.sm};
      border-color: ${theme.colors.primary};
    }
  `}
`;

export const ChecklistIcon = styled.div<{ $done: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  flex-shrink: 0;
  background: ${({ theme, $done }) => ($done ? theme.colors.success : theme.colors.surfaceHover)};
  color: ${({ theme, $done }) => ($done ? 'white' : theme.colors.textMuted)};
  border: 2px solid ${({ theme, $done }) => ($done ? theme.colors.success : theme.colors.border)};
`;

export const ChecklistStepText = styled.div`
  flex: 1;
`;

export const ChecklistStepTitle = styled.div<{ $done: boolean }>`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme, $done }) => ($done ? theme.colors.textSecondary : theme.colors.text)};
  text-decoration: ${({ $done }) => ($done ? 'line-through' : 'none')};
`;

export const ChecklistStepDesc = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: 2px;
`;

// Auto-fill block (scrape + document).
export const OrDivider = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin: ${({ theme }) => theme.spacing.md} 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSize.sm};

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
`;

export const FileInput = styled.input`
  display: none;
`;

export const FileInputLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px dashed ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: border-color 0.2s ease, color 0.2s ease;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSize.sm};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const SelectedFile = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.surfaceHover};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSize.sm};
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

export const ClearFileButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  padding: 4px;
  font-size: 16px;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.error};
  }
`;

// Mensaje de error del auto-fill (scrape/doc).
export const AutoFillErrorText = styled.p`
  margin-top: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

// Nudge banner tras un auto-fill exitoso.
export const AutoFillBanner = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.primary}15,
    ${({ theme }) => (theme.colors.secondary || theme.colors.primary)}15
  );
  border: 1px solid ${({ theme }) => theme.colors.primary}40;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
`;

export const AutoFillText = styled.div`
  flex: 1;
  min-width: 200px;
`;

export const AutoFillTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 2px;
`;

export const AutoFillDesc = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export const AutoFillActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
`;

// Grid de imágenes scrapeadas.
export const ScrapedImagesSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

export const ScrapedImagesHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

export const ScrapedImagesTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

export const SelectionInfo = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textMuted};
`;

export const ScrapedImagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.md};
`;

export const ScrapedImageItem = styled.button<{ $selected: boolean }>`
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
  padding: 0;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 2px solid ${({ theme, $selected }) => ($selected ? theme.colors.primary : 'transparent')};
  background: none;
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  &:hover {
    border-color: ${({ theme, $selected }) => ($selected ? theme.colors.primary : theme.colors.border)};
    transform: scale(1.02);
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const SelectBadge = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 22px;
  height: 22px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: bold;
  border-radius: 50%;
`;

export const ScrapedActions = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md};
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
`;

export const HelpText = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

// Shortcuts de navegación del workspace.
export const ShortcutRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
`;

// Danger zone (borrado).
export const DangerCard = styled(Card)`
  border-color: ${({ theme }) => theme.colors.error}40;
`;

export const DangerRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
`;

export const DangerText = styled.div`
  flex: 1;
  min-width: 220px;
`;
